import React from 'react';
import PropTypes from 'prop-types';
import { Box } from '@material-ui/core';
import { RadioButton, RadioButtonGroup } from 'material-ui/RadioButton';
import TimeSlots from './TimeSlots/TimeSlots';
import SpecificTime from './SpecificTime/SpecificTime';
import { recurrenceTypes } from '../../../settings/constants';
import globals from '../../../globals/communityGlobals';
import timeSlotPropType from '../../../propTypes/entities/timeSlot';

const locale = globals.getArea('treatments');

export const selectionTypes = {
  specific: 'specific',
  timeslot: 'timeslot',
  multiple: 'multiple',
};

const styles = {
  radioButton: { marginBottom: '5px' },
  radioButtonGroup: { flexDirection: 'column', width: '250px', marginLeft: 0 },
};

class TreatmentSchedule extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectionType: this.getSelectionType(props.isSpecificTime, props.isMultiple),
      treatmentSchedule: props.treatmentSchedule,
      initialTreatmentSchedule: props.treatmentSchedule,
      showWarningWillAffectVisits: this.props.hasBundledOrFutureAssignedInstances,
    };
  }

  componentWillReceiveProps = ({ isSpecificTime: newIsSpecific, isMultiple: newIsMultiple }) => {
    const { isSpecificTime: currIsSpecific, isMultiple: currIsMultiple } = this.props;
    const selectionTypeShouldBe = this.getSelectionType(newIsSpecific, newIsMultiple);
    if (
      (newIsSpecific !== currIsSpecific || newIsMultiple !== currIsMultiple) &&
      this.state.selectionType !== selectionTypeShouldBe
    ) {
      this.setState({ selectionType: selectionTypeShouldBe });
    }
  };

  onStateChange = newScheduleState => {
    this.setState({
      treatmentSchedule: { ...this.state.treatmentSchedule, ...newScheduleState },
    });
  };

  getSelectionType = (isSpecificTime, isMultiple) => {
    if (isSpecificTime) {
      return selectionTypes.specific;
    } else if (isMultiple) {
      return selectionTypes.multiple;
    }
    return selectionTypes.timeslot;
  };

  reset = () => {
    this.setState({
      selectionType: this.getSelectionType(this.props.isSpecificTime, this.props.isMultiple),
      treatmentSchedule: this.props.treatmentSchedule,
    });
  };

  handleSelectionTypeSwitch = (event, value) => {
    this.setState({ selectionType: value });
  };

  render() {
    const { selectionType, treatmentSchedule, initialTreatmentSchedule, showWarningWillAffectVisits } = this.state;
    const {
      innerRef,
      setValidity,
      readOnly,
      timeSlots,
      actionType,
      timeSlotsPackageId,
      referralDateReceived,
      referralServiceEndDate,
      isImportedFromSystmOne,
      treatmentId,
    } = this.props;
    const formsyProps = {
      onValid: setValidity(true),
      onInvalid: setValidity(false),
      innerRef,
    };

    return (
      <Box display="flex" py={1} flexDirection="column">
        <Box p={1} flexGrow={1}>
          <RadioButtonGroup
            className="row"
            style={styles.radioButtonGroup}
            name="selectionType"
            defaultSelected={selectionTypes.specific}
            onChange={this.handleSelectionTypeSwitch}
            valueSelected={selectionType}
            disabled={isImportedFromSystmOne}
          >
            <RadioButton
              value={selectionTypes.specific}
              label={locale.getMsg('specificTime')}
              disabled={readOnly || isImportedFromSystmOne}
              style={styles.radioButton}
            />
            <RadioButton
              value={selectionTypes.timeslot}
              label={locale.getMsg('timeSlot')}
              disabled={readOnly || isImportedFromSystmOne}
              style={styles.radioButton}
            />
            <RadioButton
              value={selectionTypes.multiple}
              label={locale.getMsg('multipleVisitsPerDay')}
              disabled={readOnly || isImportedFromSystmOne}
              style={styles.radioButton}
            />
          </RadioButtonGroup>
        </Box>
        <Box p={1} flexGrow={1}>
          {selectionType === selectionTypes.specific ? (
            <SpecificTime
              readOnly={readOnly}
              defaultValues={initialTreatmentSchedule}
              values={treatmentSchedule}
              actionType={actionType}
              onChange={this.onStateChange}
              referralDateReceived={referralDateReceived}
              referralServiceEndDate={referralServiceEndDate}
              isImportedFromSystmOne={isImportedFromSystmOne}
              treatmentId={treatmentId}
              {...formsyProps}
            />
          ) : (
            <TimeSlots
              readOnly={readOnly}
              defaultValues={initialTreatmentSchedule}
              values={treatmentSchedule}
              timeSlots={timeSlots}
              timeSlotsPackageId={timeSlotsPackageId}
              actionType={actionType}
              onChange={this.onStateChange}
              referralDateReceived={referralDateReceived}
              referralServiceEndDate={referralServiceEndDate}
              isImportedFromSystmOne={isImportedFromSystmOne}
              treatmentId={treatmentId}
              {...formsyProps}
            />
          )}
        </Box>
        <Box p={1} flexGrow={1}>
          {selectionType === selectionTypes.multiple && (
            <div className="row" style={{ marginLeft: '8px', color: '#e84c3d' }}>
              <p>* {locale.getMsg('multipleVisitsPerDayWarning')}</p>
            </div>
          )}
          {showWarningWillAffectVisits && (
            <div className="row" style={{ marginLeft: '8px', color: '#e84c3d' }}>
              <p>
                * {locale.getMsg('willAffectAssignedVisits')} {locale.getMsg('savingWillUnassign')}
              </p>
            </div>
          )}
        </Box>
      </Box>
    );
  }
}

TreatmentSchedule.defaultProps = {
  innerRef: () => {},
  setValidity: () => {},
  readOnly: false,
  treatmentSchedule: {
    recurEvery: 1,
    recurrenceType: recurrenceTypes.DAILY,
    dateTo: undefined,
    dateFrom: undefined,
    timeSlotsMatrix: undefined,
    monthlyRecurrenceType: undefined,
    selectedWeeks: undefined,
    selectedWeekDays: undefined,
  },
  isSpecificTime: true,
  isMultiple: false,
  timeSlotsPackageId: null,
  hasBundledOrFutureAssignedInstances: false,
};

TreatmentSchedule.propTypes = {
  setValidity: PropTypes.func,
  innerRef: PropTypes.func,
  readOnly: PropTypes.bool,
  timeSlots: PropTypes.arrayOf(timeSlotPropType).isRequired,
  treatmentSchedule: PropTypes.shape({
    recurEvery: PropTypes.number,
    recurrencePattern: PropTypes.string,
    dateTo: PropTypes.string,
    dateFrom: PropTypes.string,
    timeSlotsMatrix: PropTypes.objectOf(PropTypes.objectOf(PropTypes.bool)),
    monthlyRecurrenceType: PropTypes.string,
    selectedWeeks: PropTypes.arrayOf(PropTypes.string),
    selectedWeekDays: PropTypes.arrayOf(PropTypes.string),
  }),
  isSpecificTime: PropTypes.bool,
  isMultiple: PropTypes.bool,
  actionType: PropTypes.string.isRequired,
  timeSlotsPackageId: PropTypes.number,
  hasBundledOrFutureAssignedInstances: PropTypes.bool,
};

export default TreatmentSchedule;
